import { resolveApiOrigin } from '../../../../shared/api/resolve-api-origin';
import { logger } from '../../../../shared/logger';
import { buildPlatformUrl, resolveHostContext } from '../../../../shared/tenant-host';
import { resolveTenantRootDomain } from '../../../../shared/tenant-host/defaultRootDomain';

/**
 * 환경별 설정 — Supabase + Backend API (Vite)
 */

export interface EnvironmentConfig {
  api: {
    baseUrl: string;
    timeout: number;
    retryAttempts: number;
  };
  logging: {
    level: 'debug' | 'info' | 'warn' | 'error';
    enableConsole: boolean;
  };
  tenant: {
    defaultTenantId: string;
    enableMultiTenant: boolean;
  };
}

function resolveApiBaseUrl(): string {
  return resolveApiOrigin();
}

const developmentConfig: EnvironmentConfig = {
  api: {
    baseUrl: resolveApiBaseUrl(),
    timeout: 30_000,
    retryAttempts: 3,
  },
  logging: {
    level: 'debug',
    enableConsole: true,
  },
  tenant: {
    defaultTenantId: import.meta.env.VITE_DEFAULT_TENANT_ID || 'default_tenant',
    enableMultiTenant: import.meta.env.VITE_ENABLE_MULTI_TENANT === 'true',
  },
};

const productionConfig: EnvironmentConfig = {
  api: {
    baseUrl: resolveApiBaseUrl(),
    timeout: 60_000,
    retryAttempts: 2,
  },
  logging: {
    level: 'error',
    enableConsole: false,
  },
  tenant: {
    defaultTenantId: import.meta.env.VITE_DEFAULT_TENANT_ID || 'default_tenant',
    enableMultiTenant: import.meta.env.VITE_ENABLE_MULTI_TENANT === 'true',
  },
};

const testConfig: EnvironmentConfig = {
  api: {
    baseUrl: 'http://localhost:3001',
    timeout: 10_000,
    retryAttempts: 1,
  },
  logging: {
    level: 'warn',
    enableConsole: true,
  },
  tenant: {
    defaultTenantId: 'test_tenant',
    enableMultiTenant: true,
  },
};

const getEnvironmentConfig = (): EnvironmentConfig => {
  const env = import.meta.env.MODE;

  switch (env) {
    case 'development':
      return developmentConfig;
    case 'production':
      return productionConfig;
    case 'test':
      return testConfig;
    default:
      logger.warn(`알 수 없는 환경: ${env}, 개발 환경 설정을 사용합니다.`);
      return developmentConfig;
  }
};

export const config = getEnvironmentConfig();

export function resolveDefaultTenantId(): string {
  return getEnvironmentConfig().tenant.defaultTenantId;
}

/** Tenant host → platform owner login URL (Phase 6 operator SSO CTA). */
export function resolvePlatformLoginHref(): string {
  if (typeof window === 'undefined') {
    return '/platform/login';
  }
  const rootDomain = resolveTenantRootDomain(import.meta.env.VITE_TENANT_ROOT_DOMAIN);
  const ctx = resolveHostContext({
    hostname: window.location.hostname,
    rootDomain: import.meta.env.VITE_TENANT_ROOT_DOMAIN,
    devFallbackTenantId: resolveDefaultTenantId(),
  });
  if (ctx.kind === 'platform') {
    return '/platform/login';
  }
  return buildPlatformUrl(rootDomain, '/platform/login');
}

export const validateConfig = (): boolean => {
  const { api } = config;

  if (!api.baseUrl) {
    logger.error('❌ API base URL이 설정되지 않았습니다. (VITE_BACKEND_URL)');
    return false;
  }

  logger.debug('✅ 환경 설정이 올바르게 로드되었습니다:', {
    data: {
      environment: import.meta.env.MODE,
      apiBaseUrl: api.baseUrl,
    },
  });

  return true;
};

export default config;
